import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { PricingTiers } from "@/components/booking/PricingTiers";
import { ArrowLeft, ArrowRight, Sparkles, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { TypingAnimation } from "@/components/ui/typing-animation";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { isValidEmail } from "@/utils/security";

const problemStatements = [
  "integrate AI into their operations",
  "automate repetitive workflows",
  "scale data-driven decision making",
  "reduce operational costs with AI",
  "modernize legacy systems",
];

const STORAGE_BASE = "https://bepnmmikbflhezryppcs.supabase.co/storage/v1/object/public/avatars";

const teamAvatars = [
  { src: `${STORAGE_BASE}/tom-a`, fallback: "TA" },
  { src: `${STORAGE_BASE}/Tom`, fallback: "TM" },
  { src: `${STORAGE_BASE}/jenny`, fallback: "JN" },
  { src: `${STORAGE_BASE}/Luis`, fallback: "LP" },
];

const Consultation = () => {
  useDocumentTitle(
    "Learn More | Klexu",
    "Book a 30-minute consultation with Klexu to scope performance-based models for your industrial operation."
  );
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubscribe = async () => {
    const trimmed = email.trim();
    if (!trimmed || !isValidEmail(trimmed)) {
      toast.error("Please enter a valid email address");
      return;
    }
    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from("contact_submissions")
        .insert({ email: trimmed.toLowerCase(), source: "newsletter", newsletter_opt_in: true });
      if (error) {
        if (error.code === "23505") {
          toast.info("You're already subscribed!");
        } else {
          toast.error("Something went wrong. Please try again.");
        }
      } else {
        toast.success("You're subscribed! We'll keep you in the loop.");
        setEmail("");
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        <section className="relative pt-28 py-16 md:pt-32 md:py-24 overflow-hidden">
          {/* Background effects matching homepage */}
          <div className="absolute inset-0 bg-hero-glow opacity-70"></div>
          <div className="absolute inset-0 bg-grid-pattern opacity-[0.3] bg-[length:30px_30px]"></div>
          <div className="absolute top-1/3 right-0 w-[500px] h-[500px] bg-gradient-to-br from-accent/5 to-secondary/5 rounded-full filter blur-[120px] opacity-30"></div>
          <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-gradient-to-tr from-accent/5 to-secondary/5 rounded-full filter blur-[100px] opacity-20"></div>

          <div className="container px-4 sm:px-6 lg:px-8 mx-auto max-w-3xl text-center relative">
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-background/70 px-5 py-2 text-sm font-medium text-foreground mb-8">
              <Sparkles size={14} className="text-accent" />
              SCHEDULE A 30-MINUTE CONSULTATION
              <ArrowRight size={14} />
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground mb-6 leading-tight">
              Book your Strategy Call
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-2">
              For companies looking to
            </p>
            <div className="mb-8">
              <TypingAnimation
                texts={problemStatements}
                typingDuration={80}
                pauseDuration={1500}
                erasingDuration={40}
                className="text-lg md:text-xl font-semibold bg-gradient-to-r from-accent to-accent/70 bg-clip-text text-transparent"
              />
            </div>

            {/* Avatars row */}
            <div className="flex items-center justify-center mb-8">
              <div className="flex -space-x-3">
                {teamAvatars.map((avatar, i) => (
                  <Avatar key={i} className="h-10 w-10 border-2 border-background">
                    {avatar.src ? (
                      <AvatarImage src={avatar.src} alt={avatar.fallback} />
                    ) : null}
                    <AvatarFallback className="bg-accent text-white text-xs">
                      {avatar.fallback}
                    </AvatarFallback>
                  </Avatar>
                ))}
              </div>
              <span className="ml-3 text-sm text-muted-foreground">
                Join 200+ clients already consulting with us
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-3 max-w-lg mx-auto mb-6">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSubscribe()}
                className="h-12 bg-background border-border text-foreground placeholder:text-muted-foreground rounded-lg flex-1"
                disabled={isSubmitting}
              />
              <Button
                onClick={handleSubscribe}
                disabled={isSubmitting}
                className="h-12 px-6 bg-accent hover:bg-accent/90 text-white rounded-lg whitespace-nowrap w-full sm:w-auto"
              >
                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                JOIN OUR NEWSLETTER
              </Button>
            </div>

            <p className="text-xs text-muted-foreground max-w-md mx-auto">
              This call will provide an overview of Klexu's services and how we can help your business leverage Outcome-based solutions effectively.
            </p>
          </div>
        </section>

        <PricingTiers />
      </main>
      <Footer />
    </div>
  );
};

export default Consultation;
